import * as React from "react";
import { GetServerSideProps } from "next";
import Link from "next/link";
import toast from "react-hot-toast";
import { NextPageWithLayout } from "@pages/_app";
import AuthLayout from "@layouts/auth";
import { useForm, SubmitHandler } from "react-hook-form";
import ConfirmationForm from "@components/confirmation-page";
import InputPhone from "@components/input-phone";
import Button from "@components/button";
import { getAccessCookie, hasAccessCookie } from "@utils/storage";
import { useMutation } from "urql";
import { withUrqlClient } from "next-urql";
import {
  SignupByPhoneNumber,
  VerifyPhone,
} from "@graphql/mutations/auth.mutation";
import {
  SignupByPhoneNumberMutation,
  SignupByPhoneNumberMutationVariables,
  VerifyPhoneMutation,
  VerifyPhoneMutationVariables,
} from "@graphql/types";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useRouter } from "next/router";
import Icon from "@components/icon";
import Head from "next/head";

type RegisterInputs = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
};

type Props = {
  phoneNumber: string;
  setPhoneNumber: React.Dispatch<React.SetStateAction<string>>;
  setNextScreen: React.Dispatch<React.SetStateAction<number>>;
};

const schema = yup
  .object({
    firstName: yup
      .string()
      .min(2, "Please input a first name with at least 2 characters")
      .trim("Please remove leading and trailing spaces")
      .required("Please input your first name"),
    lastName: yup
      .string()
      .min(2, "Please input a last name with at least 2 characters")
      .trim("Please remove leading and trailing spaces")
      .required("Please input your last name"),
    email: yup
      .string()
      .email("Please input a valid email")
      .trim("Please remove leading and trailing spaces")
      .required("Please input your email"),
    password: yup
      .string()
      .min(6, "Please use a passowrd of at least 6 characters")
      .required("Please input your password"),
  })
  .required();

const RegisterForm: React.FC<Props> = ({
  phoneNumber,
  setPhoneNumber,
  setNextScreen,
}) => {
  const [type, setType] = React.useState<"password" | "text">("password");

  const [signUpResult, signUp] = useMutation<
    SignupByPhoneNumberMutation,
    SignupByPhoneNumberMutationVariables
  >(SignupByPhoneNumber);

  const [countryIsoAlpha2, setCountryIsoAlpha2] = React.useState("NG");

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm<RegisterInputs>({
    mode: "onChange",
    resolver: yupResolver(schema),
  });

  const onSubmit: SubmitHandler<RegisterInputs> = (data) => {
    signUp({
      input: {
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        phone_number: phoneNumber,
        password: data.password,
        country_iso_alpha2: countryIsoAlpha2,
      },
    })
      .then((res) => {
        if (res.error) {
          toast.error(<p className="toast">{res.error.message}</p>);
        }
        if (res.data?.signupByPhoneNumber) {
          setNextScreen(2);
        }
      })
      .catch((error) => toast.error(<p className="toast">{error?.message}</p>));
  };

  return (
    <>
      <Head>
        <title>Inawo Vendors | Register</title>
        <meta name="title" content="Inawo Vendors | Register" />
        <meta name="description" content="Register on Inawo Vendors" />
      </Head>

      <div className="px-4 w-full">
        <h1 className="font-libre text-2xl mb-3">Create an Inawo account</h1>
        <p className="text-base font-light font-sfpro">
          Sign up to sell your services
        </p>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col justify-start items-start gap-6 mt-8 xs:mt-12 w-full"
        >
          <div className="flex flex-col justify-start items-start gap-0 w-full">
            <label
              htmlFor="firstName"
              className="text-sm font-sfpro text-inawo-grey-200"
            >
              First Name
            </label>
            <input
              type="text"
              placeholder="Denzel"
              className="border-0 border-b text-base py-3 pl-0 border-inawo-grey-300 w-full focus:ring-0 focus:border-inawo-green transition"
              {...register("firstName")}
            />
            {errors.firstName?.message && (
              <p className="text-red-600 font-sfpro text-sm">
                {errors?.firstName?.message}
              </p>
            )}
          </div>
          <div className="flex flex-col justify-start items-start gap-0 w-full">
            <label
              htmlFor="lastName"
              className="text-sm font-sfpro text-inawo-grey-200"
            >
              Last Name
            </label>
            <input
              type="text"
              placeholder="Washington"
              className="border-0 border-b text-base py-3 pl-0 border-inawo-grey-300 w-full focus:ring-0 focus:border-inawo-green transition"
              {...register("lastName")}
            />
            {errors.lastName?.message && (
              <p className="text-red-600 font-sfpro text-sm">
                {errors?.lastName?.message}
              </p>
            )}{" "}
          </div>
          <InputPhone
            placeholder="+234"
            name="Phone"
            label="Phone Number(Whatsapp)"
            value={phoneNumber}
            setValue={setPhoneNumber}
            onCountryChange={setCountryIsoAlpha2}
          />
          <div className="flex flex-col justify-start items-start gap-0 w-full">
            <label
              htmlFor="email"
              className="text-sm font-sfpro text-inawo-grey-200"
            >
              Email
            </label>
            <input
              type="email"
              placeholder="abc@example.com"
              className="border-0 border-b text-base py-3 pl-0 border-inawo-grey-300 w-full focus:ring-0 focus:border-inawo-green transition"
              {...register("email")}
            />
            {errors.email?.message && (
              <p className="text-red-600 font-sfpro text-sm">
                {errors?.email?.message}
              </p>
            )}{" "}
          </div>
          <div className="flex flex-col justify-start items-start gap-0 w-full">
            <label
              htmlFor="password"
              className="text-sm font-sfpro text-inawo-grey-200"
            >
              Password (minimum of 6 characters)
            </label>
            <div className="w-full flex justify-start items-center relative">
              <input
                type={type}
                placeholder=""
                className="border-0 border-b text-base py-3 pl-0 border-inawo-grey-300 w-full focus:ring-0 focus:border-inawo-green transition"
                {...register("password")}
              />{" "}
              <div
                onClick={() =>
                  setType((prev) => (prev === "password" ? "text" : "password"))
                }
                className="absolute top-1/2 transform -translate-y-1/2 right-2 cursor-pointer"
              >
                <Icon
                  iconName={
                    type === "password"
                      ? "eyeClose"
                      : type === "text"
                      ? "eyeOpen"
                      : "eyeClose"
                  }
                />
              </div>
            </div>{" "}
            {errors.password?.message && (
              <p className="text-red-600 font-sfpro text-sm">
                {errors?.password?.message}
              </p>
            )}{" "}
          </div>
          <p className="text-center font-light text-sm text-inawo-grey mx-auto ">
            By creating an account you agree to our
            <br />
            <a
              href={`${process.env.NEXT_PUBLIC_INAWO_LANDING_SITE}/privacy`}
              target="_blank"
              rel="noreferrer noopener"
              className="font-normal underline"
            >
              Privacy Policy
            </a>{" "}
            and{" "}
            <a
              href={`${process.env.NEXT_PUBLIC_INAWO_LANDING_SITE}/terms`}
              target="_blank"
              rel="noreferrer noopener"
              className="font-normal underline"
            >
              Terms of Service
            </a>
          </p>
          <Button
            loader={signUpResult.fetching || isSubmitting}
            disabled={!isValid || isSubmitting || phoneNumber === ""}
            type="submit"
            label="Create Account"
          >
            Create Account
          </Button>
          <p className="mx-auto font-light font-sfpro text-sm">
            Already have an account?{" "}
            <span>
              <Link href="/login" passHref>
                <button className="font-medium text-inawo-green">Log in</button>
              </Link>
            </span>
          </p>{" "}
          <Link href={"/"}>
            <button className="mx-auto -mt-5 font-sfpro text-inawo-black font-light underline text-sm md:text-base">
              Skip, to explore services
            </button>
          </Link>
        </form>
      </div>
    </>
  );
};

const Register: NextPageWithLayout = () => {
  const [nextScreen, setNextScreen] = React.useState(1);
  const [confirmSignUpResult, confirmSignUp] = useMutation<
    VerifyPhoneMutation,
    VerifyPhoneMutationVariables
  >(VerifyPhone);

  const [phoneNumber, setPhoneNumber] = React.useState("");
  const router = useRouter();
  const [nextPage, setNextPage] = React.useState("");

  React.useEffect(() => {
    if (router.query.next) {
      if (router.query.next === "add-a-service") {
        setNextPage("become-a-vendor/add-a-service");
      } else {
        setNextPage(router.query.next as string);
      }

      router.replace("/login", undefined, { shallow: true });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-lg mb-12 w-full px-4">
      {nextScreen === 1 ? (
        <RegisterForm
          phoneNumber={phoneNumber}
          setPhoneNumber={setPhoneNumber}
          setNextScreen={setNextScreen}
        />
      ) : nextScreen === 2 ? (
        <ConfirmationForm
          fetching={confirmSignUpResult.fetching}
          phoneNumber={phoneNumber}
          confirmSignUp={confirmSignUp}
          setNextScreen={setNextScreen}
          nextPage={nextPage}
        />
      ) : null}
    </div>
  );
};

Register.getLayout = function getLayout(page: React.ReactElement) {
  return <AuthLayout>{page}</AuthLayout>;
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { req, res } = ctx;
  // send the user to the home page if the access token already exists
  const loggedIn = hasAccessCookie({ req, res });

  if (loggedIn) {
    return {
      redirect: {
        destination: "/",
        permanent: false,
      },
    };
  }

  return {
    props: {},
  };
};

export default withUrqlClient(
  (_ssr) => ({
    url: process.env.NEXT_PUBLIC_API_URL!,
    requestPolicy: "cache-and-network",
    fetchOptions: () => {
      return {
        headers: {
          "x-user-token": getAccessCookie() ? `${getAccessCookie()}` : "",
        },
      };
    },
  }),
  { ssr: false }
)(Register);
